import { useState } from 'react';
import clsx from 'clsx';
import {
  createRefund,
  RefundError,
  type RefundMethode,
  type RefundReden,
} from '../../lib/order-refund';

interface RefundDialogProps {
  werkOrderId: string;
  orderNumber: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (refundName: string | undefined) => void;
}

// Factuur_Correctie is hier bewust weggehaald: een correctie zonder geldstroom
// is voortaan een creditfactuur — via "Crediteren…" op de factuurpagina.
const METHODE_OPTIONS: { value: RefundMethode; label: string; helper: string }[] = [
  { value: 'Mollie', label: 'Mollie', helper: 'Automatische refund op originele betaalmethode' },
  { value: 'Bank', label: 'Bank', helper: 'Handmatige overboeking — alleen registratie' },
  { value: 'Coulance_Tegoed', label: 'Coulance-tegoed', helper: 'Tegoed voor toekomstige order' },
];

const REDEN_OPTIONS: { value: RefundReden; label: string }[] = [
  { value: 'Klacht', label: 'Klacht' },
  { value: 'Annulering', label: 'Annulering' },
  { value: 'Overbetaling', label: 'Overbetaling' },
  { value: 'Coulance', label: 'Coulance' },
  { value: 'Webshop-Retour', label: 'Webshop-retour' },
  { value: 'Overig', label: 'Overig' },
];

function makeIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `office-${crypto.randomUUID()}`;
  }
  return `office-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function RefundDialog({
  werkOrderId,
  orderNumber,
  isOpen,
  onClose,
  onSuccess,
}: RefundDialogProps) {
  const [methode, setMethode] = useState<RefundMethode>('Mollie');
  const [bedrag, setBedrag] = useState<string>('');
  const [refundReden, setRefundReden] = useState<RefundReden>('Klacht');
  const [redenVoorMollie, setRedenVoorMollie] = useState<string>('');
  const [toelichting, setToelichting] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [externalId] = useState<string>(() => makeIdempotencyKey());

  if (!isOpen) return null;

  const bedragNumber = parseFloat(bedrag.replace(',', '.'));
  const isBedragValid = Number.isFinite(bedragNumber) && bedragNumber > 0;
  const isMollie = methode === 'Mollie';
  const requiresMollieReden = isMollie && !redenVoorMollie.trim();

  const handleSubmit = async () => {
    if (!isBedragValid) {
      setError('Vul een geldig bedrag in');
      return;
    }
    if (requiresMollieReden) {
      setError('Bij Mollie is een omschrijving voor het bankafschrift verplicht');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const result = await createRefund({
        werkOrderId,
        amountIncBtw: Math.round(bedragNumber * 100) / 100,
        methode,
        refundReden,
        redenVoorMollie: redenVoorMollie.trim() || undefined,
        toelichting: toelichting.trim() || undefined,
        externalId,
      });
      onSuccess(result.refundName);
      onClose();
    } catch (err) {
      if (err instanceof RefundError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Refund mislukt');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Refund registreren</h2>
          <p className="text-xs text-slate-500 mt-1">Order: {orderNumber}</p>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-700">Methode</legend>
            <div className="space-y-1.5">
              {METHODE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={clsx(
                    'flex items-start gap-2 p-2 border rounded cursor-pointer text-sm',
                    methode === opt.value
                      ? 'border-indigo-400 bg-indigo-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  )}
                >
                  <input
                    type="radio"
                    name="refund-methode"
                    value={opt.value}
                    checked={methode === opt.value}
                    onChange={() => setMethode(opt.value)}
                    disabled={submitting}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium text-slate-900">{opt.label}</span>
                    <span className="block text-xs text-slate-500">{opt.helper}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1.5">
              Factuur corrigeren zonder terugstorting? Gebruik <strong>Crediteren…</strong> op de
              factuurpagina.
            </p>
          </fieldset>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">Bedrag (€)</span>
            <input
              type="text"
              inputMode="decimal"
              value={bedrag}
              onChange={(e) => setBedrag(e.target.value)}
              disabled={submitting}
              placeholder="0,00"
              className="mt-1 w-40 border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">Reden (categorie)</span>
            <select
              value={refundReden}
              onChange={(e) => setRefundReden(e.target.value as RefundReden)}
              disabled={submitting}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
            >
              {REDEN_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>

          {isMollie && (
            <label className="block">
              <span className="text-sm font-medium text-slate-700">
                Omschrijving op bankafschrift
              </span>
              <input
                type="text"
                value={redenVoorMollie}
                onChange={(e) => setRedenVoorMollie(e.target.value)}
                maxLength={255}
                disabled={submitting}
                placeholder="bv. 'Refund Kleda order 2026-1234'"
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
              <span className="block text-xs text-slate-500 mt-1">
                Klant ziet deze tekst op het bankafschrift.
              </span>
            </label>
          )}

          <label className="block">
            <span className="text-sm font-medium text-slate-700">
              Interne toelichting (optioneel)
            </span>
            <textarea
              value={toelichting}
              onChange={(e) => setToelichting(e.target.value)}
              rows={3}
              maxLength={1024}
              disabled={submitting}
              placeholder="Niet zichtbaar voor klant"
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </label>

          {error && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !isBedragValid || requiresMollieReden}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || !isBedragValid || requiresMollieReden
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {submitting ? 'Bezig…' : 'Refund registreren'}
          </button>
        </div>
      </div>
    </div>
  );
}
